import React from 'react';
import { Link } from 'react-router-dom';
import {
  Bookmark,
  Eye,
  Heart,
  Image as ImageIcon,
  Images,
  Pin,
  Play
} from 'lucide-react';
import type { WorkSummary } from '../../services/postService';
import { DEFAULT_AVATAR_URL, resolveAvatarUrl } from '../../constants/assets';
import ImageZoom from './ImageZoom';
import SpriteSheetPlayer from './SpriteSheetPlayer';

interface WorkCardProps {
  work: WorkSummary;
  variant: 'home' | 'profile';
  className?: string;
  onClick?: () => void;
  showEngagementStats?: boolean;
  onAppeal?: () => void;
  appealStatus?: 'pending' | 'approved' | 'rejected' | null;
}

const compactNumber = (value: number) => {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(value >= 100000 ? 0 : 1)}w`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return String(value || 0);
};

const formatDuration = (seconds?: number) => {
  if (!seconds || seconds <= 0) {
    return '';
  }

  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const getStatusBadge = (work: WorkSummary) => {
  if (work.visibility === 'draft') {
    return '草稿';
  }
  if (work.visibility === 'private') {
    return '隐藏';
  }
  if (work.uploadState === 'processing') {
    return '处理中';
  }
  if (work.uploadState === 'failed') {
    return '上传失败';
  }
  if (work.uploadState) {
    return '上传中';
  }
  // No active upload session in this tab. Server tells us where the
  // post actually is in its lifecycle. WAITING_UPLOAD means the server
  // never received valid media — usually a tab-closed-mid-upload orphan.
  if (work.auditStatus === 'WAITING_UPLOAD') {
    return '上传未完成';
  }
  if (work.auditStatus === 'PENDING') {
    return '审核中';
  }
  return '';
};

const TypeIcon = ({ work }: { work: WorkSummary }) => {
  const imageCount = work.images?.length || 0;

  if (work.type === 'video') {
    return <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />;
  }

  if (imageCount > 1) {
    return <Images className="h-4 w-4" />;
  }

  return <ImageIcon className="h-4 w-4" />;
};

const WorkStatsOverlays = ({
  work,
  showBookmark = false,
  showTypeBadge = false,
  showEngagementStats = true
}: {
  work: WorkSummary;
  showBookmark?: boolean;
  showTypeBadge?: boolean;
  showEngagementStats?: boolean;
}) => {
  const duration = formatDuration(work.duration);
  const showDuration = work.type === 'video' && !!duration;
  const showBottomOverlay = showEngagementStats || showDuration;

  return (
    <>
      {showBookmark && (
        <button
          type="button"
          aria-label="收藏作品"
          className="absolute left-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100"
          onClick={(event) => event.stopPropagation()}
        >
          <Bookmark className="h-4 w-4" />
        </button>
      )}
      {showTypeBadge && (
        <div className="absolute left-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-black/45 text-white shadow-sm backdrop-blur-sm">
          <TypeIcon work={work} />
        </div>
      )}
      {showBottomOverlay && (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
          <div className={`absolute bottom-2 left-3 right-3 z-20 flex items-center text-xs font-medium text-white ${showEngagementStats ? 'justify-between' : 'justify-end'}`}>
            {showEngagementStats && (
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5" />
                  {compactNumber(work.views)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Heart className={`h-3.5 w-3.5 ${work.liked ? 'fill-red-400 text-red-400' : 'text-white'}`} />
                  {compactNumber(work.likes)}
                </span>
              </div>
            )}
            {showDuration && (
              <span className="rounded bg-black/60 px-1.5 py-0.5 backdrop-blur-sm">
                {duration}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
};

const ProfileOverlays = ({
  work,
  showEngagementStats,
  onAppeal,
  appealStatus
}: Pick<WorkCardProps, 'work' | 'showEngagementStats' | 'onAppeal' | 'appealStatus'>) => {
  const badge = getStatusBadge(work);
  const isHidden = work.visibility === 'private';

  return (
    <>
      <WorkStatsOverlays work={work} showEngagementStats={showEngagementStats} />
      {badge && (
        <div className="absolute left-3 top-3 z-20 rounded-full bg-black/65 px-3 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          {badge}
        </div>
      )}
      {!badge && (
        <div className="absolute left-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white shadow-sm backdrop-blur-sm">
          <TypeIcon work={work} />
        </div>
      )}
      {work.isPinned && (
        <div className="absolute right-3 top-3 z-20 flex items-center gap-1 rounded-full bg-blue-500/90 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          <Pin className="h-3 w-3 fill-current" />
          <span>置顶</span>
        </div>
      )}
      {!work.uploadState && work.auditStatus !== 'REJECTED' && (
        <div className="absolute inset-0 z-10 bg-black/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      )}
      {isHidden && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-gray-200/55 backdrop-grayscale">
          <div className="-rotate-12 rounded-full border border-gray-400/60 bg-gray-100/85 px-5 py-2 text-sm font-semibold text-gray-600 shadow-sm">
            已隐藏
          </div>
        </div>
      )}
      {!work.uploadState && work.visibility !== 'draft' && work.auditStatus === 'REJECTED' && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-red-950/55 px-4 text-center backdrop-blur-sm">
          <div className="rounded-full bg-red-500 px-4 py-2 text-xs font-medium text-white shadow-lg">
            审核未通过
          </div>
          <p className="line-clamp-2 max-w-full text-[11px] leading-4 text-white/90">
            {work.rejectionReason || '作品内容未通过平台审核'}
          </p>
          {appealStatus === 'pending' ? (
            <div className="rounded-full bg-yellow-100 px-3 py-1.5 text-xs font-medium text-yellow-700 shadow">
              申诉审核中
            </div>
          ) : appealStatus === 'rejected' ? (
            <div className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 shadow">
              申诉未通过
            </div>
          ) : onAppeal && !appealStatus ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onAppeal();
              }}
              className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-800 shadow transition-colors hover:bg-white"
            >
              申诉
            </button>
          ) : null}
        </div>
      )}
    </>
  );
};

const WorkCard: React.FC<WorkCardProps> = ({
  work,
  variant,
  className = '',
  onClick,
  showEngagementStats = true,
  onAppeal,
  appealStatus
}) => {
  const aspectRatio = '4/3';
  const thumbnailUrl = work.thumbnailUrl || work.images?.[0] || '';
  const mediaClassName = variant === 'home'
    ? 'mb-0 rounded-t-[1.25rem] rounded-b-none'
    : 'mb-3 rounded-xl';

  const media = work.type === 'video' ? (
    <SpriteSheetPlayer
      thumbnailUrl={thumbnailUrl}
      spriteSheetUrl={work.spriteSheetUrl}
      spriteSheetConfig={work.spriteSheetConfig}
      aspectRatio={aspectRatio}
      className={mediaClassName}
      onClick={onClick}
      alt={work.title}
      prefetch={false}
    >
      {variant === 'home' ? (
        <WorkStatsOverlays work={work} showTypeBadge />
      ) : (
        <ProfileOverlays work={work} showEngagementStats={showEngagementStats} onAppeal={onAppeal} appealStatus={appealStatus} />
      )}
    </SpriteSheetPlayer>
  ) : (
    <ImageZoom
      src={thumbnailUrl}
      alt={work.title}
      aspectRatio={aspectRatio}
      className={mediaClassName}
      onClick={onClick}
    >
      {variant === 'home' ? (
        <WorkStatsOverlays work={work} showTypeBadge />
      ) : (
        <ProfileOverlays work={work} showEngagementStats={showEngagementStats} onAppeal={onAppeal} appealStatus={appealStatus} />
      )}
    </ImageZoom>
  );

  if (variant === 'profile') {
    const visibleTags = (work.tags || []).slice(0, 3).join(' | ');

    return (
      <div className={`group flex cursor-pointer flex-col transition-transform duration-300 ${work.uploadState ? 'opacity-80 hover:shadow-xl' : work.auditStatus === 'REJECTED' || work.visibility === 'private' ? 'hover:shadow-xl' : 'hover:-translate-y-0.5'} ${className}`}>
        {media}
        <div className="flex min-w-0 flex-col">
          <h3 className={`line-clamp-1 text-sm font-semibold transition-colors ${work.visibility === 'private' ? 'text-gray-500' : 'text-gray-900 group-hover:text-amber-600'}`}>
            {work.title}
          </h3>
          {visibleTags && (
            <div className="mt-1 line-clamp-1 text-xs text-gray-500">
              {visibleTags}
            </div>
          )}
        </div>
      </div>
    );
  }

  const creator = work.creator;
  const creatorId = creator?.id || work.creatorUserId;
  const creatorName = creator?.name || work.creatorUserId || '创作者';
  const creatorAvatar = resolveAvatarUrl(creator?.avatar || DEFAULT_AVATAR_URL);
  const visibleTags = (work.tags || []).slice(0, 3);
  const visibleTagText = visibleTags.join(' | ');

  return (
    <div
      className={`group flex h-full cursor-pointer flex-col overflow-hidden rounded-[1.25rem] border border-[oklch(0.91_0.006_250)] bg-[oklch(0.995_0.003_95)] shadow-[0_10px_28px_oklch(0.22_0.02_250_/_0.07)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[oklch(0.84_0.012_250)] hover:shadow-[0_18px_44px_oklch(0.22_0.02_250_/_0.11)] focus-within:border-[oklch(0.72_0.14_78)] ${className}`}
      onClick={onClick}
    >
      {media}
      <div className="flex min-w-0 flex-1 flex-col px-4 pb-1.5 pt-1.5">
        <h3 className="mt-auto line-clamp-1 text-[15px] font-semibold leading-5 text-gray-900 transition-colors group-hover:text-amber-700">
          {work.title}
        </h3>
        {visibleTagText && (
          <div className="mt-0.5 line-clamp-1 text-xs leading-4 text-gray-400">
            {visibleTagText}
          </div>
        )}
        <div className="mt-1 flex min-w-0 items-center gap-2">
          <Link to={`/profile/${creatorId}`} onClick={(event) => event.stopPropagation()} className="shrink-0">
            <img
              src={creatorAvatar}
              alt={creatorName}
              className="h-5 w-5 rounded-full border border-[oklch(0.96_0.004_95)] object-cover shadow-[0_4px_10px_oklch(0.22_0.02_250_/_0.12)]"
            />
          </Link>
          <Link
            to={`/profile/${creatorId}`}
            onClick={(event) => event.stopPropagation()}
            className="truncate text-xs font-medium leading-4 text-gray-900 transition-colors hover:text-gray-950"
          >
            {creatorName}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WorkCard;
